import React from "react";
import ImageWrapper from "../ImageComponent/ImageWrapper";
import {PortableText} from "@portabletext/react";
import Link from "next/link";

// Improved components for blog card content
const components = {
  types: {},
  marks: {
    // The whole card is already a link; nested <a> tags are invalid HTML
    link: ({ children }) => <span>{children}</span>,
    strong: ({ children }) => (
      <span className="font-semibold text-gray-800">{children}</span>
    ),
    em: ({ children }) => (
      <em className="italic">{children}</em>
    ),
  },
  block: {
    div: ({children}) => (
      <div className="mb-2">{children}</div>
    ),
    h1: ({children}) => (
      <h1 className="text-base font-semibold text-gray-800 mb-1">{children}</h1>
    ),
    h2: ({children}) => (
      <h2 className="text-sm font-semibold text-gray-800 mb-1">{children}</h2>
    ),
    h3: ({children}) => (
      <h3 className="text-sm font-medium text-gray-700 mb-1">{children}</h3>
    ),
    h4: ({children}) => (
      <h4 className="text-sm font-medium text-gray-700 mb-1">{children}</h4>
    ),
    p: ({children}) => (
      <p className="text-sm text-gray-600 leading-relaxed mb-1 last:mb-0">{children}</p>
    )
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside text-sm text-gray-600 ml-2">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside text-sm text-gray-600 ml-2">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="leading-relaxed">{children}</li>
    ),
    number: ({ children }) => (
      <li className="leading-relaxed">{children}</li>
    ),
  },
};

const BlogDataBox = (data) => {
  const {
    slug,
    mainImageSrc,
    mainImageAlt,
    title,
    description,
    author,
    date,
    icon1Src,
    icon1Alt,
    icon2Src,
    icon2Alt,
  } = data;

  return (
    <Link href={`/vidya/${slug}`} className="group cursor-pointer block h-full">
      <div className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 ease-in-out overflow-hidden border border-gray-100 h-full flex flex-col group-hover:border-BluntRed transform group-hover:-translate-y-2">

        {/* Image Section */}
        <div className="relative overflow-hidden h-48 sm:h-52">
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <ImageWrapper
            src={mainImageSrc}
            alt={mainImageAlt}
            className="object-cover w-full h-full transform group-hover:scale-110 transition-transform duration-700 ease-out"
          />

          {/* Read Time Badge */}
          <div className="absolute top-4 right-4 z-20">
                        <span className="bg-white/90 backdrop-blur-sm text-xs font-medium text-gray-700 px-3 py-1 rounded-full shadow-sm">
                            5 min read
                        </span>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 flex flex-col p-6">

          {/* Title */}
          <h3 className="text-lg md:text-xl font-bold text-gray-900 leading-tight mb-3 group-hover:text-BluntRed transition-colors duration-300 line-clamp-2">
            {title}
          </h3>

          {/* Description */}
          <div className="flex-1 text-gray-600 text-sm md:text-base leading-relaxed mb-4">
            <div className="line-clamp-3">
              <PortableText
                value={description}
                components={components}
              />
            </div>
          </div>

          {/* Author and Date Section */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                {author && (
                  <p className="text-sm font-medium text-gray-900">
                    {author}
                  </p>
                )}
                {date && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {date}
                  </p>
                )}
              </div>
            </div>

            {/* Action Icons */}
            <div className="flex items-center space-x-2">
              {icon1Src && (
                <div className="w-8 h-8 rounded-full bg-gray-100 hover:bg-BluntRed p-1.5 transition-colors duration-200">
                  <ImageWrapper src={icon1Src} alt={icon1Alt} className="w-full h-full object-contain"/>
                </div>
              )}
              {icon2Src && (
                <div className="w-8 h-8 rounded-full bg-gray-100 hover:bg-BluntRed p-1.5 transition-colors duration-200">
                  <ImageWrapper src={icon2Src} alt={icon2Alt} className="w-full h-full object-contain"/>
                </div>
              )}

              {/* Read More Arrow */}
              <div className="w-8 h-8 rounded-full bg-BluntRed text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BlogDataBox;